import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Injector, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatSelectChange } from '@angular/material/select';
import { ArcElement, BarController, BarElement, CategoryScale, Chart, Filler, Legend, LinearScale, LineController, LineElement, PieController, PointElement, Tooltip } from 'chart.js';
import { AbstractComponent, Customer, DashboardCrmKpis, DashboardCrmService, DateUtil, Funnel, FunnelService, GoalStatus } from 'lib-trend-core';
import { takeUntil } from 'rxjs';

interface ProductPercentage {
  name: string;
  percentage: number;
}

/** One day of the revenue chart: `day` is the company's calendar day as `yyyy-MM-dd` (SPEC-2026-10 RF-06). */
interface RevenueChartDay {
  wonValue: number;
  wonCount: number;
  lostValue: number;
  lostCount: number;
  /** The company's midnight of the day, as an ISO instant — formatted in the company's zone. */
  date: string;
}

interface RevenueChartPoint {
  days: RevenueChartDay[];
  overall: {
    totalValue: number;
    totalCount: number;
  };
}

interface RevenueByLeadSourceData {
  name: string;
  budget: number;
  customer: Customer;
  count?: number;
}

Chart.register([
  CategoryScale,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  PieController,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
]);

/** One reading of "O que falta para o CRM mostrar dinheiro". */
interface CrmDataGap {
  /** Stable key: the title and the sentence are translated from it. */
  key: string;
  /** The headline, already formatted — a count pair, a ratio or a percentage. */
  value: string;
  status: GoalStatus;
  /** Values the translated sentence interpolates. */
  params: Record<string, string | number>;
}

@Component({
    selector: 'app-dashboard-crm',
    templateUrl: './dashboard-crm.component.html',
    styleUrl: './dashboard-crm.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DashboardCrmComponent extends AbstractComponent implements AfterViewInit {
  @ViewChild('picker', { static: false }) picker: MatDatepicker<Date>;
  @ViewChild('chartCanvas', { static: false }) chartCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('chartRevenueByLeadSource', { static: false }) chartRevenueByLeadSource: ElementRef<HTMLCanvasElement>;

  public isPicker: boolean = false;

  public kpis: DashboardCrmKpis | null = null;
  public kpisPercentage: DashboardCrmKpis | null = null;
  public customers: Array<Record<string, string | number>> = [];
  public products: Array<Record<string, string | number>> = [];
  public users: Array<Record<string, string | number>> = [];
  public revenueChartData: RevenueChartPoint | null = null;
  public revenueByLeadSourceData: Array<RevenueByLeadSourceData> = [];

  public funnelList: Array<Funnel> = [];
  public labelFilterFunnel: string = '';

  public productsPercentage: Array<ProductPercentage> = [];

  private revenueChart: Chart | null = null;
  private revenueByLeadSourceChart: Chart | null = null;
  private chartReady: boolean = false;
  private readonly selectedFunnelStorageKey = 'kanbanSelectedFunnelId';

  constructor(
    injector: Injector,
    private dashboardCrmService: DashboardCrmService,
    private funnelService: FunnelService,
    private cdr: ChangeDetectorRef,
  ) {
    super(injector);
    this.setupForm();
  }

  ngOnInit(): void {
    this.getDashboardKpis();
    this.getDashboardCustomers();
    this.getDashboardProducts();
    this.getDashboardUser();
    this.getRevenueChart();
    this.getRevenueByLeadSource();
    this.getFunnelList();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.revenueChart?.destroy();
    this.revenueChart = null;
    this.revenueByLeadSourceChart?.destroy();
    this.revenueByLeadSourceChart = null;
  }

  ngAfterViewInit(): void {
    this.chartReady = true;
    if (this.revenueChartData?.days?.length) {
      this.buildRevenueChartData();
    }
    if (this.revenueByLeadSourceData?.length) {
      this.buildRevenueByLeadSourceChartData();
    }
  }

  private setupForm(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    this.formGroup = this.formBuilder.group({
      funnel: [null],
      dateRange: this.formBuilder.group({
        dateStart: [thirtyDaysAgo],
        dateEnd: [today],
      }),
    });

    let previousValue = this.formGroup.value;
    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((currentValue) => {
      const dateStartChanged = previousValue.dateRange.dateStart !== currentValue.dateRange.dateStart;
      const dateEndChanged = previousValue.dateRange.dateEnd !== currentValue.dateRange.dateEnd;
      const funnelChanged = previousValue.funnel !== currentValue.funnel;
      if (dateStartChanged || dateEndChanged || funnelChanged) {
        this.loadFilters();
      }
      previousValue = currentValue;
    });
  }

  getDashboardKpis(): void {
    const formValues = this.formGroup.getRawValue();

    /**
     * The period travels as two CALENDAR DAYS in `dateStart` / `dateEnd` (SPEC-2026-10 RF-05 ·
     * ADR-001 — the attendance dashboard's contract), never as instants: the server resolves them
     * in the company's timezone, so the figures do not depend on where the browser is. Won and
     * lost figures follow the closing moment, the total the creation.
     */
    const filters: Record<string, unknown> = {
      funnel: formValues.funnel,
      ...(formValues.dateRange?.dateStart && formValues.dateRange?.dateEnd
        ? {
          dateStart: DateUtil.toCalendarDay(formValues.dateRange.dateStart),
          dateEnd: DateUtil.toCalendarDay(formValues.dateRange.dateEnd),
        }
        : {})
    };

    this.searchParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, unknown>);

    this.dashboardCrmService.getDashboardCrmKpis(this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe((kpis: any) => {
        this.kpis = kpis.current;
        this.kpisPercentage = kpis.percentageChange;
      });
  }

  getDashboardCustomers(): void {
    this.dashboardCrmService.getDashboardCustomers(this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe((customers: Array<Record<string, string | number>>) => {
        this.customers = customers;
      });
  }

  getFunnelList(): void {
    const storedFunnelId = this.getStoredFunnelId();
    this.funnelService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe((funnels) => {
        this.funnelList = funnels;
        if (storedFunnelId) {
          const storedFunnel = funnels.find(f => f._id === storedFunnelId);
          if (storedFunnel) {
            this.searchByFunnel(storedFunnel);
          }
        }
      });
  }

  searchByFunnel(funnel: Funnel): void {
    if (funnel) {
      this.labelFilterFunnel = funnel.name;
      this.formGroup.get('funnel')?.setValue(funnel._id);
    } else {
      this.labelFilterFunnel = '';
      this.formGroup.get('funnel')?.setValue(null);
    }
  }

  getDashboardProducts(): void {
    this.dashboardCrmService.getDashboardProducts(this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe((products: Array<Record<string, string | number>>) => {
        this.products = products;
        this.getPercentageUserTotalWonValue();
      });
  }

  getDashboardUser(): void {
    this.dashboardCrmService.getDashboardUser(this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: Array<Record<string, string | number>>) => {
        this.users = users;
      });
  }

  getRevenueChart(): void {
    this.dashboardCrmService.getRevenueChart(this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe((revenueChartData: any) => {
        this.revenueChartData = revenueChartData ?? null;

        this.cdr.detectChanges();
        if (this.chartReady && this.chartCanvas?.nativeElement) {
          this.buildRevenueChartData();
        }
      });
  }

  getRevenueByLeadSource(): void {
    this.dashboardCrmService.getRevenueByLeadSource(this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        this.revenueByLeadSourceData = data ?? [];
        this.cdr.detectChanges();
        if (this.chartReady && this.chartRevenueByLeadSource?.nativeElement) {
          this.buildRevenueByLeadSourceChartData();
        }
      });
  }

  onPeriodChange(event: MatSelectChange) {
    const today = new Date();
    const period = parseInt(event.value, 10);

    if (!isNaN(period)) {
      if (period === 1) {
        this.formGroup.get('dateRange')?.patchValue({
          dateStart: new Date(),
          dateEnd: new Date(),
        });
      } else if ([30, 60, 120].includes(period)) {
        this.formGroup.get('dateRange')?.patchValue({
          dateStart: new Date(today.setDate(today.getDate() - period)),
          dateEnd: new Date(),
        });
      }
      this.picker?.close();
    }
  }

  openPicker(): void {
    this.isPicker = true;
    setTimeout(() => {
      this.picker.open();
    }, 300);
  }

  convertPercentage(value: number): string {
    return (value * 100).toFixed(2) + '%';
  }

  converteCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  buildRevenueChartData(): void {
    if (!this.chartReady || !this.chartCanvas?.nativeElement) return;

    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) return;

    this.revenueChart?.destroy();
    this.revenueChart = null;

    const days = this.revenueChartData?.days ?? [];
    if (!days.length) return;

    // `date` is the company's midnight of each day as an ISO instant, so string order is date
    // order; the label is formatted in the company's zone, never in the browser's.
    const ordered = [...days].sort((a, b) => a.date.localeCompare(b.date));

    const formatCurrency = (value: number) =>
      new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

    const labels = ordered.map(e => DateUtil.toFormart(e.date, 'dd/MM', this.getTimezoneCompanyCurrentUser()));
    const wonValues = ordered.map(e => e.wonValue);
    const lostValues = ordered.map(e => e.lostValue);
    const overallTotalValue = this.revenueChartData?.overall?.totalValue ?? 0;
    const overallTotalCount = this.revenueChartData?.overall?.totalCount ?? 0;
    const overallValues = ordered.map(() => overallTotalValue);

    this.revenueChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: this.t('dashboard.crm.chartGains'),
            data: wonValues,
            borderColor: '#0acf97',
            backgroundColor: 'rgba(10, 207, 151, 0.10)',
            fill: true,
            tension: 0.35,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: '#0acf97',
            pointBorderWidth: 2,
          },
          {
            label: this.t('dashboard.crm.chartLosses'),
            data: lostValues,
            borderColor: '#dc3545',
            backgroundColor: 'rgba(220, 53, 69, 0.10)',
            fill: true,
            tension: 0.35,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: '#dc3545',
            pointBorderWidth: 2,
          },
          {
            label: this.t('dashboard.crm.chartOpportunities'),
            data: overallValues,
            borderColor: '#2a7cf7',
            backgroundColor: 'rgba(42, 124, 247, 0.10)',
            fill: true,
            tension: 0.35,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: '#2a7cf7',
            pointBorderWidth: 2,
            borderDash: [5, 5],
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: { boxWidth: 16, boxHeight: 12 },
          },
          tooltip: {
            enabled: true,
            backgroundColor: 'rgba(17, 17, 17, 0.95)',
            bodyColor: '#fff',
            borderWidth: 2,
            padding: 12,
            displayColors: false,
            titleFont: { size: 14, weight: 'bold' },
            bodyFont: { size: 13 },
            bodySpacing: 8,
            boxPadding: 8,
            cornerRadius: 6,
            callbacks: {
              title: (ctx) => `${ctx[0].label}`,
              label: (ctx) => {
                const point = ordered[ctx.dataIndex];
                const value = formatCurrency(Number(ctx.raw) || 0);
                if (ctx.datasetIndex === 0) {
                  return [this.t('dashboard.crm.tooltipWonTotal', { value }), this.t('dashboard.crm.tooltipWonCount', { count: point.wonCount })];
                } else if (ctx.datasetIndex === 1) {
                  return [this.t('dashboard.crm.tooltipLostTotal', { value }), this.t('dashboard.crm.tooltipLostCount', { count: point.lostCount })];
                } else {
                  return [this.t('dashboard.crm.tooltipOpportunitiesValue', { value }), this.t('dashboard.crm.tooltipOpportunitiesCount', { count: overallTotalCount })];
                }
              },
              afterLabel: () => '',
            },
          },
        },
        scales: {
          x: { grid: { display: false }, ticks: { maxRotation: 0 } },
          y: {
            beginAtZero: true,
            ticks: { callback: (value) => formatCurrency(Number(value)) },
          },
        },
      },
    });
  }

  buildRevenueByLeadSourceChartData(): void {
    if (!this.chartReady || !this.chartRevenueByLeadSource?.nativeElement) return;

    const ctx = this.chartRevenueByLeadSource.nativeElement.getContext('2d');
    if (!ctx) return;

    this.revenueByLeadSourceChart?.destroy();
    this.revenueByLeadSourceChart = null;

    if (!this.revenueByLeadSourceData?.length) return;

    const buckets = new Map<string, { leadSource: string; budget: number; count: number }>();

    for (const entry of this.revenueByLeadSourceData) {
      const leadSource = entry.customer?.contact?.leadSource;
      const key = leadSource?.name ?? this.t('dashboard.crm.noLeadSource');
      const bucket = buckets.get(key) ?? { leadSource: key, budget: 0, count: 0 };
      bucket.budget += entry.budget ?? 0;
      bucket.count += 1;
      buckets.set(key, bucket);
    }

    const ordered = Array.from(buckets.values()).sort((a, b) => b.budget - a.budget);
    const labels = ordered.map(b => b.leadSource);
    const budgets = ordered.map(b => b.budget);
    const counts = ordered.map(b => b.count);

    const formatCurrency = (value: number) =>
      new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

    const colors = [
      '#2a7cf7', '#0acf97', '#ffc107', '#dc3545', '#6f42c1',
      '#20c997', '#e83e8c', '#fd7e14', '#6610f2', '#17a2b8',
    ];

    this.revenueByLeadSourceChart = new Chart(ctx, {
      type: 'pie',
      data: {
        labels,
        datasets: [{
          label: this.t('dashboard.crm.chartRevenue'),
          data: budgets,
          backgroundColor: colors.slice(0, budgets.length),
          borderColor: '#fff',
          borderWidth: 2,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'right',
            labels: { boxWidth: 16, boxHeight: 12, padding: 15 },
          },
          tooltip: {
            enabled: true,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: '#2a7cf7',
            borderWidth: 2,
            padding: 10,
            displayColors: false,
            titleFont: { size: 13, weight: 'bold', family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" },
            bodyFont: { size: 12, family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" },
            bodySpacing: 4,
            boxPadding: 6,
            callbacks: {
              title: (ctx) => ctx[0].label,
              label: (ctx) => {
                const count = counts[ctx.dataIndex];
                const budget = formatCurrency(Number(ctx.raw) || 0);
                return [budget, this.t('dashboard.crm.tooltipOpportunitiesCount', { count })];
              },
            },
          },
        },
      },
    });
  }

  getPercentageUserTotalWonValue(): void {
    if (!this.products || this.products.length === 0) {
      this.productsPercentage = [];
      return;
    }

    const totalValue = this.products.reduce((sum, product) => {
      return sum + (product['soldValue'] as number || 0);
    }, 0);

    if (!totalValue) {
      this.productsPercentage = [];
      return;
    }

    this.productsPercentage = this.products.slice(0, 5).map(product => ({
      name: product['productName'] as string,
      percentage: ((product['soldValue'] as number) / totalValue) * 100,
    }));
  }

  private getStoredFunnelId(): string | null {
    try {
      return localStorage.getItem(this.selectedFunnelStorageKey);
    } catch {
      return null;
    }
  }

  private loadFilters(): void {
    this.getDashboardKpis();
    this.getDashboardCustomers();
    this.getDashboardProducts();
    this.getDashboardUser();
    this.getRevenueChart();
    this.getRevenueByLeadSource();
  }

  // ---------------------------------------------------------------------------
  // Change against the previous period
  // ---------------------------------------------------------------------------

  /**
   * The change of one figure against the previous period, or `null` when the API found no
   * baseline to compare against — a period whose predecessor was empty has no percentage, and
   * the card shows no arrow rather than a meaningless "+100%".
   */
  private change(key: string): number | null {
    const raw = (this.kpisPercentage as unknown as Record<string, unknown> | null)?.[key];
    const value = Number(raw);
    return raw === null || raw === undefined || !Number.isFinite(value) ? null : value;
  }

  hasChange(key: string): boolean {
    const value = this.change(key);
    return value !== null && value !== 0;
  }

  /**
   * Green when the figure moved the way the business wants it to. `higherIsBetter` is false for
   * losses: more deals lost than last period is not an improvement, however green an arrow is.
   */
  changeClass(key: string, higherIsBetter: boolean = true): string {
    const value = this.change(key);
    if (value === null || value === 0) {
      return 'text-muted';
    }
    const improved = value > 0 ? higherIsBetter : !higherIsBetter;
    return improved ? 'text-success' : 'text-danger';
  }

  changeIcon(key: string): string {
    const value = this.change(key);
    if (value === null || value === 0) {
      return '';
    }
    return value > 0 ? 'mdi-arrow-up-bold' : 'mdi-arrow-down-bold';
  }

  /** The magnitude only — the arrow already carries the direction. */
  formatChange(key: string): string {
    const value = this.change(key);
    return value === null ? '' : `${Math.abs(value).toFixed(0)}%`;
  }

  // ---------------------------------------------------------------------------
  // What the CRM needs filled in before it can show money
  // ---------------------------------------------------------------------------

  /**
   * Why the money figures above look the way they do.
   *
   * A pipeline reporting R$ 0,00 of revenue is almost never a company that sold nothing: it is a
   * company whose won deals were closed without a value typed in, or whose deals have no owner
   * and no origin. Without these readings the operator sees a zero and concludes the dashboard
   * is broken; with them the screen says which field is missing and where.
   *
   * A reading only appears when it has something to say — a CRM with every field filled in shows
   * none of them, and the section disappears.
   */
  get crmDataGaps(): Array<CrmDataGap> {
    const kpis = this.kpis;
    if (!kpis) {
      return [];
    }

    const gaps: Array<CrmDataGap> = [];

    if (kpis.wonOpportunities > 0 && kpis.wonWithoutValue > 0) {
      const all = kpis.wonWithoutValue >= kpis.wonOpportunities;
      gaps.push({
        key: 'wonWithoutValue',
        value: this.t('dashboard.crm.gapOf', { count: kpis.wonWithoutValue, total: kpis.wonOpportunities }),
        status: all ? 'CRITICAL' : 'ATTENTION',
        params: { count: kpis.wonWithoutValue, total: kpis.wonOpportunities },
      });
    }

    if (kpis.lossOpportunities > kpis.wonOpportunities && kpis.lossOpportunities > 0) {
      gaps.push({
        key: 'lostAgainstWon',
        value: this.t('dashboard.crm.gapRatio', { lost: kpis.lossOpportunities, won: Math.max(kpis.wonOpportunities, 1) }),
        status: kpis.lossOpportunities >= kpis.wonOpportunities * 2 ? 'CRITICAL' : 'ATTENTION',
        params: {
          lost: kpis.lossOpportunities,
          won: kpis.wonOpportunities,
          withoutValue: kpis.lostWithoutValue,
        },
      });
    }

    const concentration = this.valueConcentration;
    if (concentration && concentration.percent >= 70) {
      gaps.push({
        key: 'valueConcentration',
        value: this.formatPercentValue(concentration.percent),
        status: concentration.percent >= 90 ? 'CRITICAL' : 'ATTENTION',
        params: {
          name: concentration.name,
          value: this.formatCurrency(concentration.value),
          total: this.formatCurrency(concentration.total),
        },
      });
    }

    if (kpis.withoutOwner > 0) {
      gaps.push({
        key: 'withoutOwner',
        value: this.t('dashboard.crm.gapOf', { count: kpis.withoutOwner, total: kpis.totalOpportunities }),
        status: 'ATTENTION',
        params: { count: kpis.withoutOwner, total: kpis.totalOpportunities },
      });
    }

    if (kpis.withoutOrigin > 0) {
      const all = kpis.withoutOrigin >= kpis.totalOpportunities;
      gaps.push({
        key: 'withoutOrigin',
        value: all
          ? this.t('dashboard.crm.gapNoOrigin')
          : this.t('dashboard.crm.gapOf', { count: kpis.withoutOrigin, total: kpis.totalOpportunities }),
        status: 'ATTENTION',
        params: { count: kpis.withoutOrigin, total: kpis.totalOpportunities },
      });
    }

    return gaps;
  }

  gapTitle(gap: CrmDataGap): string {
    return this.t('dashboard.crm.gap' + gap.key.charAt(0).toUpperCase() + gap.key.slice(1) + 'Title', gap.params);
  }

  gapText(gap: CrmDataGap): string {
    return this.t('dashboard.crm.gap' + gap.key.charAt(0).toUpperCase() + gap.key.slice(1) + 'Text', gap.params);
  }

  gapStatusClass(status: GoalStatus): string {
    return status === 'CRITICAL' ? 'is-err' : 'is-warn';
  }

  gapTextClass(status: GoalStatus): string {
    return status === 'CRITICAL' ? 'text-danger' : 'text-warning';
  }

  // ---------------------------------------------------------------------------
  // How much of the pipeline sits with each person
  // ---------------------------------------------------------------------------

  /** The attendants holding pipeline value, biggest first, with "no owner" as a row of its own. */
  get pipelineByAttendant(): Array<{ name: string; value: number; share: number }> {
    const rows = (this.users ?? [])
      .map((row) => ({
        name: this.attendantName(row),
        value: Number(row['totalOpportunitiesValue'] ?? 0),
      }))
      .sort((a, b) => b.value - a.value);

    const biggest = rows.length ? rows[0].value : 0;
    return rows.map((row) => ({ ...row, share: biggest > 0 ? (row.value / biggest) * 100 : 0 }));
  }

  /** Who holds most of the pipeline, and how much of it — `null` when there is nothing to read. */
  private get valueConcentration(): { name: string; value: number; total: number; percent: number } | null {
    const rows = this.pipelineByAttendant;
    const total = rows.reduce((sum, row) => sum + row.value, 0);
    if (!rows.length || total <= 0) {
      return null;
    }
    const top = rows[0];
    return { name: top.name, value: top.value, total, percent: (top.value / total) * 100 };
  }

  /** The aggregation answers "-" for deals nobody owns; the screen names that case. */
  attendantName(row: Record<string, string | number>): string {
    const name = String(row['userName'] ?? '').trim();
    return !name || name === '-' ? this.t('dashboard.crm.withoutOwner') : name;
  }

  formatPercentValue(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return '—';
    }
    return `${value.toFixed(1).replace('.', ',')}%`;
  }

  private formatCurrency(value: number): string {
    return (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
  }
}
